import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ShieldOff, Plus, Calendar, Megaphone, Star } from "lucide-react";

const CATEGORIES = {
  general: "Général",
  harcelement: "Harcèlement",
  salaire: "Salaire / Paie",
  securite: "Sécurité au travail",
  autre: "Autre",
};

const STATUS = {
  open: { label: "Ouverte", color: "#E60000" },
  in_review: { label: "En cours", color: "#D97706" },
  resolved: { label: "Résolue", color: "#059669" },
  rejected: { label: "Rejetée", color: "#868E96" },
};

export default function SalarieSyndicat() {
  const [syndicats, setSyndicats] = useState([]);
  const [actualites, setActualites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ syndicat_id: "", title: "", description: "", category: "general", anonymous: false });

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/salarie/syndicats-actifs"),
        api.get("/salarie/actualites-syndicat"),
      ]);
      setSyndicats(s.data);
      setActualites(a.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/salarie/plaintes", form);
      toast.success("Plainte envoyée au syndicat. Vous serez recontacté(e).");
      setOpen(false);
      setForm({ syndicat_id: "", title: "", description: "", category: "general", anonymous: false });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (syndicats.length === 0) {
    return (
      <div className="min-h-full">
        <PageHeader eyebrow="Syndicat" title="Espace syndical" sub="Plaintes anonymes et actualités syndicales." />
        <div className="p-6 lg:p-10">
          <div className="bg-white border border-[#DEE2E6] p-10 text-center max-w-2xl" data-testid="syndicat-not-affiliated">
            <ShieldOff className="h-12 w-12 mx-auto mb-4 text-[#DEE2E6]" />
            <h3 className="font-display text-xl font-bold mb-2">Aucun syndicat affilié</h3>
            <p className="text-sm text-[#495057]">Votre entreprise n'est actuellement affiliée à aucun syndicat sur PAG. Demandez à votre représentant du personnel.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Syndicat"
        title="Espace syndical"
        sub="Consultez les actualités et déposez vos plaintes (anonymes possibles)."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="plainte-create-btn" className="h-11 bg-[#E60000] hover:bg-[#B91C1C] text-white"><AlertTriangle className="h-4 w-4 mr-2" /> Déposer une plainte</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#E60000]" /> Déposer une plainte</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="plainte-create-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Syndicat destinataire</Label>
                  <Select value={form.syndicat_id} onValueChange={(v) => setForm({ ...form, syndicat_id: v })}>
                    <SelectTrigger data-testid="plainte-syndicat" className="h-11"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      {syndicats.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="plainte-cat" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Sujet</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="plainte-title" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Description détaillée</Label>
                  <Textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="plainte-desc" />
                </div>
                <div className="flex items-center gap-2 p-3 border border-[#DEE2E6] bg-[#F8F9FA]">
                  <input type="checkbox" id="anonymous" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} data-testid="plainte-anonymous" className="h-4 w-4 accent-[#002FA7]" />
                  <label htmlFor="anonymous" className="text-xs font-semibold cursor-pointer flex items-center gap-1.5"><ShieldOff className="h-3 w-3" /> Rester anonyme (votre nom ne sera pas transmis au syndicat)</label>
                </div>
                <DialogFooter><Button type="submit" disabled={!form.syndicat_id || !form.title || !form.description} data-testid="plainte-submit" className="bg-[#E60000] text-white">Envoyer la plainte</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10 space-y-6">
        {/* Syndicats affiliés */}
        <div className="bg-white border border-[#DEE2E6] p-5" data-testid="syndicats-affilies">
          <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3">Syndicats actifs dans votre entreprise</div>
          <div className="flex flex-wrap gap-2">
            {syndicats.map((s) => (
              <div key={s.id} className="px-3 py-1.5 bg-[#0A0A0A] text-white text-xs font-semibold flex items-center gap-2">
                <span className="text-[#FFD700]">{s.code}</span>
                <span>·</span>
                <span>{s.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actualités */}
        <div data-testid="actualites-list">
          <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#002FA7]" /> Actualités syndicales</div>
          {actualites.length === 0 ? (
            <div className="bg-white border border-[#DEE2E6] p-8 text-center text-sm text-[#868E96]">
              Aucune actualité publiée pour le moment.
            </div>
          ) : (
            <div className="space-y-3">
              {actualites.map((a) => (
                <div key={a.id} className={`bg-white border ${a.important ? "border-[#FFD700] border-2" : "border-[#DEE2E6]"} p-5`} data-testid={`actualite-${a.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {a.important && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#FFD700] text-[#0A0A0A] px-1.5 py-0.5 flex items-center gap-1"><Star className="h-2.5 w-2.5" /> Important</span>}
                    <span className="text-[10px] text-[#868E96] flex items-center gap-1.5 font-mono"><Calendar className="h-3 w-3" /> {new Date(a.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold">{a.title}</h3>
                  <div className="text-sm text-[#495057] mt-2 whitespace-pre-wrap">{a.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
